// screens/WeeklyReportScreen.js - Screen 10
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from './HomeScreen';
import { SyncBar, EmptyState } from '../components/shared';
import api from '../utils/api';

export default function WeeklyReportScreen() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flagFilter, setFlagFilter] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => { loadReport(); }, [flagFilter, fromDate, toDate]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = { from: fromDate, to: toDate };
      if (flagFilter) params.flag = flagFilter;
      const { data } = await api.get('/api/reports/weekly', { params });
      setOrders(data);
    } catch {} finally { setLoading(false); }
  };

  const grouped = orders.reduce((acc, o) => {
    const flag = o.has_claim ? 'Has Claim' : o.used_for_marketing ? 'Marketing' : 'Management Flag';
    if (!acc[flag]) acc[flag] = [];
    acc[flag].push(o);
    return acc;
  }, {});

  const handleExportPDF = () => {
    alert('In production: generates PDF grouped by flag type with week header.\nEndpoint: /api/reports/weekly/pdf');
  };

  const handleExportExcel = () => {
    alert('In production: generates Excel with columns:\nOrder Number · Product Name · Flag Type · Date Flagged · Flagged By · Comments Summary\nEndpoint: /api/reports/weekly/excel');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>←</button>
        <h2>Weekly Report</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={handleExportPDF}>PDF</button>
          <button className="btn btn-secondary btn-sm" onClick={handleExportExcel}>XLS</button>
        </div>
      </div>

      <div className="page-content">
        {/* Filters */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">From</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input-field" />
            </div>
            <div className="form-group">
              <label className="form-label">To</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="input-field" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['', 'claim', 'marketing', 'management'].map((f, i) => (
              <button key={i} onClick={() => setFlagFilter(f)}
                className={`btn btn-sm ${flagFilter === f ? 'btn-primary' : 'btn-secondary'}`}>
                {f === '' ? 'All flags' : f === 'claim' ? 'Has Claim' : f === 'marketing' ? 'Marketing' : 'Management'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner spinner-lg" /></div>
        ) : orders.length === 0 ? (
          <EmptyState icon="📊" title="No flagged orders" message="No orders match the selected filters for this period" />
        ) : (
          <div>
            <div style={{ marginBottom: 16, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              {orders.length} flagged order{orders.length !== 1 ? 's' : ''} in period
            </div>
            {Object.entries(grouped).map(([flag, flagOrders]) => (
              <div key={flag} style={{ marginBottom: 24 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
                  paddingBottom: 8, borderBottom: '1px solid var(--border-subtle)'
                }}>
                  <div style={{
                    width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                    background: flag === 'Has Claim' ? 'var(--accent-danger)' :
                      flag === 'Marketing' ? 'var(--accent-primary)' : 'var(--accent-warning)'
                  }} />
                  <h3 style={{ fontSize: '1rem' }}>{flag}</h3>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>({flagOrders.length})</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {flagOrders.map(o => (
                    <div key={o.id} className="card card-hover" style={{ padding: '12px 14px' }}
                      onClick={() => navigate(`/order/${o.id}`)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--accent-primary)', fontSize: '0.9375rem' }}>
                            {o.order_number}
                          </span>
                          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: 2 }}>{o.product_name}</div>
                          {o.latest_comment && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                              "{o.latest_comment.substring(0, 80)}..."
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-muted)', flexShrink: 0, marginLeft: 10 }}>
                          <div>{o.flagged_by_name}</div>
                          <div>{o.flagged_at ? new Date(o.flagged_at).toLocaleDateString() : ''}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav active="search" />
    </div>
  );
}
